import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../api/axiosInstance';

export interface AdmitCardSubject {
  _id?: string;
  studentId: string;
  subjectId: string;
  subjectName: string;
  subjectCode: string;
  year?: string;
  semester?: string;
  examDate: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdmitCardSubjectResponse {
  status: boolean;
  message: string;
  data?: AdmitCardSubject | AdmitCardSubject[];
}

export interface CreateAdmitCardSubjectRequest {
  studentId: string;
  subjectId: string;
  subjectName: string;
  subjectCode: string;
  year?: string;
  semester?: string;
  examDate: string;
}

export interface UpdateAdmitCardSubjectRequest {
  subjectId: string;
  year?: string;
  semester?: string;
  examDate: string;
}

// Get admit card subjects for a student
const getAdmitCardSubjects = async (studentId: string): Promise<AdmitCardSubject[]> => {
  const response = await axiosInstance.get<AdmitCardSubjectResponse>(
    `/api/admin/admit-card-subjects/${studentId}`
  );
  return (response.data.data as AdmitCardSubject[]) || [];
};

// Create admit card subject
const createAdmitCardSubject = async (
  data: CreateAdmitCardSubjectRequest
): Promise<AdmitCardSubjectResponse> => {
  const response = await axiosInstance.post('/api/admin/admit-card-subjects', data);
  return response.data;
};

// Update admit card subject
const updateAdmitCardSubject = async (
  subjectId: string,
  data: UpdateAdmitCardSubjectRequest
): Promise<AdmitCardSubjectResponse> => {
  const response = await axiosInstance.put(`/api/admin/admit-card-subjects/${subjectId}`, data);
  return response.data;
};

// Delete admit card subject
const deleteAdmitCardSubject = async (subjectId: string): Promise<AdmitCardSubjectResponse> => {
  const response = await axiosInstance.delete(`/api/admin/admit-card-subjects/${subjectId}`);
  return response.data;
};

// Hook to get admit card subjects for a student
export const useAdmitCardSubjects = (studentId: string) => {
  return useQuery<AdmitCardSubject[], Error>({
    queryKey: ['admitCardSubjects', studentId],
    queryFn: () => getAdmitCardSubjects(studentId),
    enabled: !!studentId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
  });
};

// Hook to create admit card subject
export const useCreateAdmitCardSubject = () => {
  const queryClient = useQueryClient();

  return useMutation<AdmitCardSubjectResponse, Error, CreateAdmitCardSubjectRequest>({
    mutationFn: createAdmitCardSubject,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admitCardSubjects', variables.studentId] });
      queryClient.invalidateQueries({ queryKey: ['student', variables.studentId] });
    },
  });
};

// Hook to update admit card subject
export const useUpdateAdmitCardSubject = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AdmitCardSubjectResponse,
    Error,
    { subjectId: string; data: UpdateAdmitCardSubjectRequest; studentId: string }
  >({
    mutationFn: ({ subjectId, data }) => updateAdmitCardSubject(subjectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admitCardSubjects', variables.studentId] });
      queryClient.invalidateQueries({ queryKey: ['student', variables.studentId] });
    },
  });
};

// Hook to delete admit card subject
export const useDeleteAdmitCardSubject = () => {
  const queryClient = useQueryClient();

  return useMutation<AdmitCardSubjectResponse, Error, { subjectId: string; studentId: string }>({
    mutationFn: ({ subjectId }) => deleteAdmitCardSubject(subjectId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admitCardSubjects', variables.studentId] });
      queryClient.invalidateQueries({ queryKey: ['student', variables.studentId] });
    },
  });
};

